import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { firebaseAuth } from '../../providers/AuthProvider';
import { toast } from 'react-toastify';
import {
  getAuth,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  TotpMultiFactorGenerator,
} from 'firebase/auth';

const SignIn = () => {
  const { inputs, setInputs, errors, setErrors, user } = useContext(firebaseAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const useQuery = new URLSearchParams(window.location.search);

  const [resolver, setResolver] = useState(null);
  const [totpHint, setTotpHint] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.isAuthenticated) {
      const fromPath = location.state?.from?.pathname;
      navigate(fromPath || '/manage', { replace: true });
    }
  }, [user, navigate, location]);

  useEffect(() => {
    const pwUpdate = useQuery.get('pw_update');
    const reauth = useQuery.get('reauth');
    if (pwUpdate) toast.success('Password set successfully. Please sign in below.');
    if (reauth === 'mfa') {
      toast.info('Please sign in again to continue setting up your authenticator app.');
    }
    setInputs({ email: '', password: '', pw_update: !!pwUpdate });
    setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, inputs.email, inputs.password);
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const r = getMultiFactorResolver(auth, error);
        const hint = r.hints.find((h) => h.factorId === 'totp');
        if (!hint) {
          toast.error(
            'Your account uses an MFA method that is no longer supported. ' +
              'Contact support@core12.com to reset your MFA configuration.',
          );
        } else {
          setResolver(r);
          setTotpHint(hint);
        }
      } else if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/user-not-found'
      ) {
        toast.error('Email or password is incorrect.');
      } else {
        console.error('Sign-in failed', error);
        toast.error(error.message || 'Sign-in failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!resolver || !totpHint) return;
    if (!/^\d{6}$/.test(totpCode.trim())) {
      toast.error('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSubmitting(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        totpCode.trim(),
      );
      await resolver.resolveSignIn(assertion);
    } catch (error) {
      console.error('MFA challenge failed', error);
      toast.error(
        error.code === 'auth/invalid-verification-code'
          ? 'That code did not match. Try again with the current code from your app.'
          : error.message || 'Could not verify code.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setInputs((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
        {resolver ? 'Two-factor authentication' : 'Sign in'}
      </h1>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
        {resolver
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Sign in to continue to Porsche eZentrum.'}
      </p>

      {!resolver && (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              onChange={handleChange}
              className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              onChange={handleChange}
              className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-sm">
            <Link to="/manage/forgot-password" className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white">
              Forgot password?
            </Link>
          </div>
          {errors[0] && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20">
              {errors[0]}
            </div>
          )}
        </form>
      )}

      {resolver && totpHint && (
        <form onSubmit={handleCodeSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="totpCode" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              6-digit code
            </label>
            <input
              id="totpCode"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-center text-lg tracking-widest text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            {submitting ? 'Verifying…' : 'Verify and sign in'}
          </button>
        </form>
      )}
    </div>
  );
};

export default SignIn;
